/**
 * Onboarding Chat - Conversational profile setup for athletes
 *
 * A warm, complete onboarding that collects useful profile data
 * while feeling like a friendly conversation.
 */

import { useState, useEffect, useRef } from 'react';
import { Send, Check } from 'lucide-react';
import { updateProfile } from '@tss/shared';

interface OnboardingChatProps {
  userName?: string;
  onComplete: () => void;
  onSkip: () => void;
}

type StepId =
  | 'welcome'
  | 'name'
  | 'position'
  | 'jersey'
  | 'dominant_hand'
  | 'level'
  | 'grad_year'
  | 'goals'
  | 'environment'
  | 'equipment'
  | 'duration'
  | 'learning'
  | 'feedback'
  | 'motivation'
  | 'schedule'
  | 'limitations'
  | 'complete';

interface ChatMessage {
  id: string;
  type: 'coach' | 'user';
  content: string;
  options?: OptionItem[];
  multiSelect?: boolean;
  inputType?: 'text' | 'name' | 'number';
  inputPlaceholder?: string;
}

interface OptionItem {
  label: string;
  value: string;
  selected?: boolean;
}

interface ProfileData {
  given_name: string;
  family_name: string;
  position: string;
  jersey_number: string;
  dominant_hand: string;
  target_level: string;
  graduation_year: number | null;
  learning_style: string;
  motivation_type: string;
  preferred_feedback_style: string;
  preferred_workout_time: string;
  preferred_workout_duration: string;
  training_environment: string;
  equipment_available: string[];
  athletic_goals: string[];
  current_limitations: string[];
}

const POSITIONS: OptionItem[] = [
  { label: 'Point Guard', value: 'Point Guard' },
  { label: 'Shooting Guard', value: 'Shooting Guard' },
  { label: 'Small Forward', value: 'Small Forward' },
  { label: 'Power Forward', value: 'Power Forward' },
  { label: 'Center', value: 'Center' },
];

const GOALS: OptionItem[] = [
  { label: 'Shooting', value: 'Improve shooting' },
  { label: 'Ball handling', value: 'Better ball handling' },
  { label: 'Defense', value: 'Stronger defense' },
  { label: 'Speed & agility', value: 'Speed and agility' },
  { label: 'Strength', value: 'Build strength' },
  { label: 'Game IQ', value: 'Basketball IQ' },
  { label: 'Finishing', value: 'Finishing at the rim' },
  { label: 'Passing', value: 'Court vision & passing' },
];

const LEARNING_STYLES: OptionItem[] = [
  { label: 'Watch and learn', value: 'visual' },
  { label: 'Get my reps in', value: 'kinesthetic' },
  { label: 'Talk it through', value: 'auditory' },
  { label: 'Study film & diagrams', value: 'reading' },
];

const MOTIVATIONS: OptionItem[] = [
  { label: 'I love to compete', value: 'competition' },
  { label: 'I want to keep improving', value: 'improvement' },
  { label: 'I do it for my team', value: 'team' },
  { label: 'I just love the game', value: 'fun' },
];

const SCHEDULE: OptionItem[] = [
  { label: 'Morning person', value: 'morning' },
  { label: 'Afternoon works best', value: 'afternoon' },
  { label: 'Evening grinder', value: 'evening' },
  { label: 'Whenever I can', value: 'flexible' },
];

const DOMINANT_HAND: OptionItem[] = [
  { label: 'Right hand', value: 'right' },
  { label: 'Left hand', value: 'left' },
  { label: 'Both!', value: 'ambidextrous' },
];

const TARGET_LEVEL: OptionItem[] = [
  { label: 'Just for fun', value: 'recreational' },
  { label: 'High school ball', value: 'high_school' },
  { label: 'College level', value: 'college' },
  { label: 'Going pro', value: 'professional' },
];

const FEEDBACK_STYLE: OptionItem[] = [
  { label: 'Keep it real with me', value: 'direct' },
  { label: 'Encourage me along the way', value: 'encouraging' },
  { label: 'Give me all the details', value: 'detailed' },
];

const ENVIRONMENT: OptionItem[] = [
  { label: 'At home / driveway', value: 'home' },
  { label: 'Gym or rec center', value: 'gym' },
  { label: 'Outdoor courts', value: 'outdoor' },
  { label: 'School gym', value: 'school' },
];

const EQUIPMENT: OptionItem[] = [
  { label: 'Basketball', value: 'basketball' },
  { label: 'Hoop', value: 'hoop' },
  { label: 'Cones', value: 'cones' },
  { label: 'Resistance bands', value: 'resistance_bands' },
  { label: 'Jump rope', value: 'jump_rope' },
  { label: 'Weights', value: 'weights' },
  { label: 'Agility ladder', value: 'agility_ladder' },
];

const DURATION: OptionItem[] = [
  { label: 'Quick sessions (15-30 min)', value: 'short' },
  { label: 'Regular workouts (30-60 min)', value: 'medium' },
  { label: 'Long training (60+ min)', value: 'long' },
];

const LIMITATIONS: OptionItem[] = [
  { label: 'Knee issues', value: 'knee' },
  { label: 'Ankle issues', value: 'ankle' },
  { label: 'Back problems', value: 'back' },
  { label: 'Shoulder issues', value: 'shoulder' },
  { label: 'Wrist/hand', value: 'wrist' },
  { label: "None - I'm good!", value: 'none' },
];

export function OnboardingChat({ userName, onComplete, onSkip }: OnboardingChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentStep, setCurrentStep] = useState<StepId>('welcome');
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [selectedLimitations, setSelectedLimitations] = useState<string[]>([]);
  const [profileData, setProfileData] = useState<ProfileData>({
    given_name: '',
    family_name: '',
    position: '',
    jersey_number: '',
    dominant_hand: '',
    target_level: '',
    graduation_year: null,
    learning_style: '',
    motivation_type: '',
    preferred_feedback_style: '',
    preferred_workout_time: '',
    preferred_workout_duration: '',
    training_environment: '',
    equipment_available: [],
    athletic_goals: [],
    current_limitations: [],
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Start conversation
  useEffect(() => {
    if (userName) {
      const [first, ...rest] = userName.split(' ');
      setProfileData((prev) => ({
        ...prev,
        given_name: first || '',
        family_name: rest.join(' ') || '',
      }));
      addCoachMessage(
        `Hey ${first || userName}! Let's get you set up. This'll only take a minute.`,
        undefined,
        false,
        undefined,
        undefined,
        () => {
          setTimeout(() => {
            addCoachMessage('What position do you play?', POSITIONS);
            setCurrentStep('position');
          }, 600);
        }
      );
    } else {
      addCoachMessage(
        "Hey! Welcome aboard. Let's set up your profile real quick.",
        undefined,
        false,
        undefined,
        undefined,
        () => {
          setTimeout(() => {
            addCoachMessage("First off, what's your name?", undefined, false, 'name', 'Your name');
            setCurrentStep('name');
          }, 500);
        }
      );
    }
  }, []);

  function addCoachMessage(
    content: string,
    options?: OptionItem[],
    multiSelect?: boolean,
    inputType?: 'text' | 'name' | 'number',
    inputPlaceholder?: string,
    onDone?: () => void
  ) {
    setIsTyping(true);
    const delay = 300 + Math.random() * 200;
    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now()}`,
          type: 'coach',
          content,
          options,
          multiSelect,
          inputType,
          inputPlaceholder,
        },
      ]);
      onDone?.();
      setTimeout(() => inputRef.current?.focus(), 100);
    }, delay);
  }

  function addUserMessage(content: string) {
    setMessages((prev) => [
      ...prev,
      {
        id: `msg-${Date.now()}`,
        type: 'user',
        content,
      },
    ]);
  }

  function handleSingleSelect(option: OptionItem) {
    addUserMessage(option.label);

    switch (currentStep) {
      case 'position':
        setProfileData((prev) => ({ ...prev, position: option.value }));
        setTimeout(() => {
          addCoachMessage(
            `${option.label}, nice! What's your jersey number?`,
            undefined,
            false,
            'number',
            '#'
          );
          setCurrentStep('jersey');
        }, 200);
        break;

      case 'dominant_hand':
        setProfileData((prev) => ({ ...prev, dominant_hand: option.value }));
        setTimeout(() => {
          addCoachMessage('What level are you playing at?', TARGET_LEVEL);
          setCurrentStep('level');
        }, 200);
        break;

      case 'level':
        setProfileData((prev) => ({ ...prev, target_level: option.value }));
        if (option.value === 'high_school' || option.value === 'college') {
          setTimeout(() => {
            addCoachMessage(
              'What year do you graduate?',
              undefined,
              false,
              'number',
              'Graduation year'
            );
            setCurrentStep('grad_year');
          }, 200);
        } else {
          setTimeout(() => {
            addCoachMessage('What are you working on? Pick as many as you want.', GOALS, true);
            setCurrentStep('goals');
          }, 200);
        }
        break;

      case 'environment':
        setProfileData((prev) => ({ ...prev, training_environment: option.value }));
        setTimeout(() => {
          addCoachMessage('What equipment do you have access to?', EQUIPMENT, true);
          setCurrentStep('equipment');
        }, 200);
        break;

      case 'duration':
        setProfileData((prev) => ({ ...prev, preferred_workout_duration: option.value }));
        setTimeout(() => {
          addCoachMessage('How do you learn best?', LEARNING_STYLES);
          setCurrentStep('learning');
        }, 200);
        break;

      case 'learning':
        setProfileData((prev) => ({ ...prev, learning_style: option.value }));
        setTimeout(() => {
          addCoachMessage('How do you like to get feedback?', FEEDBACK_STYLE);
          setCurrentStep('feedback');
        }, 200);
        break;

      case 'feedback':
        setProfileData((prev) => ({ ...prev, preferred_feedback_style: option.value }));
        setTimeout(() => {
          addCoachMessage('What keeps you going?', MOTIVATIONS);
          setCurrentStep('motivation');
        }, 200);
        break;

      case 'motivation':
        setProfileData((prev) => ({ ...prev, motivation_type: option.value }));
        setTimeout(() => {
          addCoachMessage('When do you usually like to train?', SCHEDULE);
          setCurrentStep('schedule');
        }, 200);
        break;

      case 'schedule':
        setProfileData((prev) => ({ ...prev, preferred_workout_time: option.value }));
        setTimeout(() => {
          addCoachMessage(
            'Almost done! Any injuries or limitations we should know about?',
            LIMITATIONS,
            true
          );
          setCurrentStep('limitations');
        }, 200);
        break;
    }
  }

  function handleGoalToggle(goal: OptionItem) {
    setSelectedGoals((prev) => {
      if (prev.includes(goal.value)) {
        return prev.filter((g) => g !== goal.value);
      }
      return [...prev, goal.value];
    });
  }

  function handleGoalsConfirm() {
    if (selectedGoals.length === 0) return;

    const goalLabels = GOALS.filter((g) => selectedGoals.includes(g.value))
      .map((g) => g.label)
      .join(', ');

    addUserMessage(goalLabels);
    setProfileData((prev) => ({ ...prev, athletic_goals: selectedGoals }));

    setTimeout(() => {
      addCoachMessage('Love the focus! Where do you usually train?', ENVIRONMENT);
      setCurrentStep('environment');
    }, 200);
  }

  function handleEquipmentToggle(item: OptionItem) {
    setSelectedEquipment((prev) => {
      if (prev.includes(item.value)) {
        return prev.filter((e) => e !== item.value);
      }
      return [...prev, item.value];
    });
  }

  function handleEquipmentConfirm() {
    if (selectedEquipment.length === 0) {
      addUserMessage('Nothing special');
    } else {
      const labels = EQUIPMENT.filter((e) => selectedEquipment.includes(e.value))
        .map((e) => e.label)
        .join(', ');
      addUserMessage(labels);
    }
    setProfileData((prev) => ({ ...prev, equipment_available: selectedEquipment }));

    setTimeout(() => {
      addCoachMessage('How long do you usually train for?', DURATION);
      setCurrentStep('duration');
    }, 200);
  }

  function handleLimitationsToggle(item: OptionItem) {
    if (item.value === 'none') {
      setSelectedLimitations(['none']);
    } else {
      setSelectedLimitations((prev) => {
        const filtered = prev.filter((l) => l !== 'none');
        if (filtered.includes(item.value)) {
          return filtered.filter((l) => l !== item.value);
        }
        return [...filtered, item.value];
      });
    }
  }

  function handleLimitationsConfirm() {
    const hasNone = selectedLimitations.includes('none') || selectedLimitations.length === 0;

    if (hasNone) {
      addUserMessage("I'm good!");
    } else {
      const labels = LIMITATIONS.filter((l) => selectedLimitations.includes(l.value))
        .map((l) => l.label)
        .join(', ');
      addUserMessage(labels);
    }

    const limitations = hasNone ? [] : selectedLimitations;
    const finalProfile = { ...profileData, current_limitations: limitations };
    setProfileData(finalProfile);
    finishOnboarding(finalProfile);
  }

  function handleNameSubmit() {
    if (!inputValue.trim()) return;
    const nameParts = inputValue.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    addUserMessage(inputValue.trim());
    setProfileData((prev) => ({ ...prev, given_name: firstName, family_name: lastName }));
    setInputValue('');

    setTimeout(() => {
      addCoachMessage(`Nice to meet you, ${firstName}! What position do you play?`, POSITIONS);
      setCurrentStep('position');
    }, 200);
  }

  function handleJerseySubmit() {
    const jersey = inputValue.trim() || 'none';
    addUserMessage(jersey === 'none' ? 'No number yet' : `#${jersey}`);
    setProfileData((prev) => ({ ...prev, jersey_number: jersey === 'none' ? '' : jersey }));
    setInputValue('');

    setTimeout(() => {
      addCoachMessage('Left-handed or right-handed?', DOMINANT_HAND);
      setCurrentStep('dominant_hand');
    }, 200);
  }

  function handleGradYearSubmit() {
    const year = parseInt(inputValue.trim());
    if (year && year >= 2024 && year <= 2035) {
      addUserMessage(inputValue.trim());
      setProfileData((prev) => ({ ...prev, graduation_year: year }));
    } else {
      addUserMessage('Skipped');
    }
    setInputValue('');

    setTimeout(() => {
      addCoachMessage('What are you working on? Pick as many as you want.', GOALS, true);
      setCurrentStep('goals');
    }, 200);
  }

  function finishOnboarding(finalProfile: ProfileData) {
    setCurrentStep('complete');

    setTimeout(async () => {
      try {
        await updateProfile(finalProfile);
      } catch (err) {
        console.error('Failed to save:', err);
      }
      addCoachMessage(
        "You're all set! Your coaches will use this to personalize your training. Let's get to work!",
        undefined,
        false,
        undefined,
        undefined,
        () => {
          setTimeout(onComplete, 1200);
        }
      );
    }, 200);
  }

  function handleInputSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (currentStep === 'name') {
      handleNameSubmit();
    } else if (currentStep === 'jersey') {
      handleJerseySubmit();
    } else if (currentStep === 'grad_year') {
      handleGradYearSubmit();
    }
  }

  const lastMsg = messages[messages.length - 1];
  const showInput = lastMsg?.inputType && currentStep !== 'complete';
  const showGoals = lastMsg?.multiSelect && currentStep === 'goals';
  const showEquipment = lastMsg?.multiSelect && currentStep === 'equipment';
  const showLimitations = lastMsg?.multiSelect && currentStep === 'limitations';
  const showSingleOptions = lastMsg?.options && !lastMsg?.multiSelect && currentStep !== 'complete';

  return (
    <div className="chat-overlay" onClick={onSkip}>
      <div className="chat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chat-messages">
          {messages.map((msg) => (
            <div key={msg.id} className={`chat-row ${msg.type}`}>
              {msg.type === 'coach' && <div className="chat-avatar">🏀</div>}
              <div className="chat-bubble">{msg.content}</div>
            </div>
          ))}

          {/* Single-select options */}
          {showSingleOptions && lastMsg?.options && (
            <div className="chat-options">
              {lastMsg.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleSingleSelect(opt)}
                  className="chat-opt"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Multi-select goals */}
          {showGoals && lastMsg?.options && (
            <div className="chat-goals">
              <div className="chat-goals-grid">
                {lastMsg.options.map((opt) => {
                  const isSelected = selectedGoals.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleGoalToggle(opt)}
                      className={`chat-goal ${isSelected ? 'selected' : ''}`}
                    >
                      {isSelected && <Check size={14} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {selectedGoals.length > 0 && (
                <button onClick={handleGoalsConfirm} className="chat-confirm">
                  Continue with {selectedGoals.length} selected
                </button>
              )}
            </div>
          )}

          {/* Multi-select equipment */}
          {showEquipment && lastMsg?.options && (
            <div className="chat-goals">
              <div className="chat-goals-grid">
                {lastMsg.options.map((opt) => {
                  const isSelected = selectedEquipment.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleEquipmentToggle(opt)}
                      className={`chat-goal ${isSelected ? 'selected' : ''}`}
                    >
                      {isSelected && <Check size={14} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <button onClick={handleEquipmentConfirm} className="chat-confirm">
                {selectedEquipment.length > 0
                  ? `Continue with ${selectedEquipment.length} selected`
                  : 'Continue'}
              </button>
            </div>
          )}

          {/* Multi-select limitations */}
          {showLimitations && lastMsg?.options && (
            <div className="chat-goals">
              <div className="chat-goals-grid">
                {lastMsg.options.map((opt) => {
                  const isSelected = selectedLimitations.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleLimitationsToggle(opt)}
                      className={`chat-goal ${isSelected ? 'selected' : ''}`}
                    >
                      {isSelected && <Check size={14} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <button onClick={handleLimitationsConfirm} className="chat-confirm">
                {selectedLimitations.length > 0 && !selectedLimitations.includes('none')
                  ? `Continue with ${selectedLimitations.length} selected`
                  : 'Continue'}
              </button>
            </div>
          )}

          {isTyping && (
            <div className="chat-row coach">
              <div className="chat-avatar">🏀</div>
              <div className="chat-bubble chat-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showInput && (
          <form className="chat-input" onSubmit={handleInputSubmit}>
            <input
              ref={inputRef}
              type={lastMsg?.inputType === 'number' ? 'text' : 'text'}
              inputMode={lastMsg?.inputType === 'number' ? 'numeric' : 'text'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={lastMsg?.inputPlaceholder || 'Type here...'}
              autoFocus
            />
            <button type="submit" disabled={currentStep === 'name' && !inputValue.trim()}>
              <Send size={18} />
            </button>
          </form>
        )}

        {currentStep === 'jersey' && (
          <button
            className="chat-skip-jersey"
            onClick={() => {
              setInputValue('');
              handleJerseySubmit();
            }}
          >
            Skip — no number yet
          </button>
        )}

        {currentStep === 'grad_year' && (
          <button
            className="chat-skip-jersey"
            onClick={() => {
              setInputValue('');
              handleGradYearSubmit();
            }}
          >
            Skip this
          </button>
        )}

        {currentStep !== 'complete' && currentStep !== 'jersey' && currentStep !== 'grad_year' && (
          <button className="chat-skip" onClick={onSkip}>
            Skip for now
          </button>
        )}
      </div>

      <style>{`
        .chat-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 1000;
          padding: 0;
        }
        @media (min-width: 480px) {
          .chat-overlay {
            align-items: center;
            padding: 20px;
          }
        }
        .chat-modal {
          background: white;
          width: 100%;
          max-width: 420px;
          max-height: 100vh;
          display: flex;
          flex-direction: column;
          border-radius: 24px 24px 0 0;
          box-shadow: 0 -4px 40px rgba(0, 0, 0, 0.15);
          overflow: hidden;
        }
        @media (min-width: 480px) {
          .chat-modal {
            border-radius: 24px;
            max-height: 80vh;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          }
        }
        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 28px 18px 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-height: 300px;
        }
        .chat-row {
          display: flex;
          align-items: flex-end;
          gap: 10px;
          max-width: 90%;
          animation: msgIn 0.25s ease;
        }
        @keyframes msgIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .chat-row.coach {
          align-self: flex-start;
        }
        .chat-row.user {
          align-self: flex-end;
          flex-direction: row-reverse;
        }
        .chat-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: var(--gray-100);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 18px;
        }
        .chat-bubble {
          padding: 12px 16px;
          border-radius: 20px;
          font-size: 15px;
          line-height: 1.5;
        }
        .chat-row.coach .chat-bubble {
          background: var(--gray-100);
          color: var(--gray-900);
          border-bottom-left-radius: 6px;
        }
        .chat-row.user .chat-bubble {
          background: var(--purple-600);
          color: white;
          border-bottom-right-radius: 6px;
        }
        .chat-typing {
          display: flex;
          gap: 5px;
          padding: 14px 18px;
        }
        .chat-typing span {
          width: 6px;
          height: 6px;
          background: var(--gray-400);
          border-radius: 50%;
          animation: typeBounce 1s ease-in-out infinite;
        }
        .chat-typing span:nth-child(2) { animation-delay: 0.12s; }
        .chat-typing span:nth-child(3) { animation-delay: 0.24s; }
        @keyframes typeBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }

        /* Single-select options */
        .chat-options {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 8px 0 12px 44px;
          animation: optionsIn 0.2s ease;
        }
        @keyframes optionsIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .chat-opt {
          background: white;
          border: 1.5px solid var(--gray-200);
          border-radius: 18px;
          padding: 9px 16px;
          font-size: 14px;
          color: var(--gray-700);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .chat-opt:hover {
          border-color: var(--purple-600);
          color: var(--purple-600);
          background: rgba(var(--purple-rgb), 0.04);
        }

        /* Multi-select goals */
        .chat-goals {
          padding: 8px 0 12px 44px;
          animation: optionsIn 0.2s ease;
        }
        .chat-goals-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 12px;
        }
        .chat-goal {
          background: white;
          border: 1.5px solid var(--gray-200);
          border-radius: 18px;
          padding: 9px 14px;
          font-size: 13px;
          color: var(--gray-600);
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .chat-goal:hover {
          border-color: var(--gray-300);
        }
        .chat-goal.selected {
          background: rgba(var(--purple-rgb), 0.1);
          border-color: var(--purple-600);
          color: var(--purple-600);
        }
        .chat-confirm {
          background: var(--purple-600);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 10px 18px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.15s ease;
        }
        .chat-confirm:hover {
          opacity: 0.9;
        }

        /* Input */
        .chat-input {
          display: flex;
          gap: 10px;
          padding: 14px 18px;
          border-top: 1px solid var(--gray-100);
          background: var(--gray-50);
        }
        .chat-input input {
          flex: 1;
          background: white;
          border: 1.5px solid var(--gray-200);
          border-radius: 22px;
          padding: 12px 18px;
          font-size: 15px;
          color: var(--gray-900);
          outline: none;
          transition: border-color 0.15s ease;
        }
        .chat-input input:focus {
          border-color: var(--purple-600);
        }
        .chat-input input::placeholder {
          color: var(--gray-400);
        }
        .chat-input button {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          border: none;
          background: var(--purple-600);
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          flex-shrink: 0;
        }
        .chat-input button:disabled {
          opacity: 0.35;
          cursor: default;
        }

        /* Skip buttons */
        .chat-skip, .chat-skip-jersey {
          background: none;
          border: none;
          color: var(--gray-400);
          font-size: 14px;
          padding: 14px;
          cursor: pointer;
          transition: color 0.15s ease;
          text-align: center;
        }
        .chat-skip:hover, .chat-skip-jersey:hover {
          color: var(--gray-600);
        }
        .chat-skip-jersey {
          border-top: 1px solid var(--gray-100);
          background: var(--gray-50);
        }
      `}</style>
    </div>
  );
}

export default OnboardingChat;
